class SizeGuideModalController {
  constructor() {
    this.activeCategory = 'mens';
    this.activeUnit = 'cm';
    this.contentData = '';
    // Track units per category for the page layout
    this.categoryUnits = {
      'mens': 'in',
      'womens': 'in',
      'boys': 'in',
      'girls': 'in'
    };
    this.#init();
  }

  #init() {
    const ready = () => this.#setup();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', ready);
    } else {
      ready();
    }
  }

  #setup() {
    const dataEl = document.getElementById('size-guide-data');
    if (dataEl) this.contentData = dataEl.innerHTML || '';

    // Check if we're on the page layout (multiple sections) or modal layout (single section)
    const isPageLayout = document.querySelectorAll('[data-category-content]').length > 1;

    if (isPageLayout) {
      this.#setupPageLayout();
    } else {
      this.#setupModalLayout();
    }
  }

  #setupPageLayout() {
    // For page layout: render all categories at once, each with its own unit toggle
    const categories = ['mens', 'womens', 'boys', 'girls'];

    categories.forEach(category => {
      // Setup unit toggles for this category
      const unitButtons = document.querySelectorAll(`[data-category-unit="${category}"] button[data-unit]`);
      unitButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          const unit = btn.getAttribute('data-unit');
          if (!unit) return;
          this.categoryUnits[category] = unit;
          this.#updateActiveButtons(`[data-category-unit="${category}"] button[data-unit]`, 'data-unit', unit);
          this.#renderCategory(category);
        });
      });

      // Initialize active state for this category's unit toggle
      const activeBtn = document.querySelector(`[data-category-unit="${category}"] button[data-unit="${this.categoryUnits[category]}"]`);
      if (activeBtn) this.#moveActiveState(activeBtn);

      // Initial render for this category
      this.#renderCategory(category);
    });
  }

  #setupModalLayout() {
    // Original modal behavior with category tabs
    const categoryButtons = document.querySelectorAll('button[data-category]');
    
    categoryButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const category = btn.getAttribute('data-category');
        if (!category) return;
        this.activeCategory = category;
        this.#updateActiveButtons('button[data-category]', 'data-category', category);
        this.#syncCategorySelect();
        this.#render();
      });
    });

    const select = document.getElementById('size-guide-category');
    if (select) {
      select.addEventListener('change', () => {
        const value = select.value;
        if (!value) return;
        this.activeCategory = value;
        this.#updateActiveButtons('button[data-category]', 'data-category', value);
        this.#render();
      });
      // initialize select to current active
      select.value = this.activeCategory;
    }

    document.querySelectorAll('button[data-unit]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const unit = btn.getAttribute('data-unit');
        if (!unit) return;
        this.activeUnit = unit;
        this.#updateActiveButtons('button[data-unit]', 'data-unit', unit);
        this.#render();
      });
    });

    this.#render();
    this.#initActiveStates();
  }

  #syncCategorySelect() {
    const select = document.getElementById('size-guide-category');
    if (select) select.value = this.activeCategory;
  }

  #updateActiveButtons(selector, attr, value) {
    // Limit search to the correct toggle-buttons group
    // Find any button with the requested value, then scope to that group
    const targetBtn = document.querySelector(`${selector}[${attr}="${value}"]`);
    if (!targetBtn) return;
    
    const group = targetBtn.closest('.toggle-buttons');
    if (!group) {
      // Try using parentElement as fallback
      const parent = targetBtn.parentElement;
      if (parent && parent.classList.contains('toggle-buttons')) {
        this.#updateButtonsInGroup(parent, selector, attr, value);
        return;
      }
      return;
    }

    this.#updateButtonsInGroup(group, selector, attr, value);
  }

  #updateButtonsInGroup(group, selector, attr, value) {
    group.querySelectorAll(selector).forEach((b) => {
      b.classList.toggle('active', b.getAttribute(attr) === value);
    });

    // Force reflow before calculating new position
    const activeButton = group.querySelector(`${selector}.active`);
    if (activeButton) {
      // Force browser to recalculate layout before moving active state
      void activeButton.offsetWidth;
      this.#moveActiveState(activeButton);
    }

    // Update modal image when category changes
    if (attr === 'data-category') {
      this.#updateModalImage(value);
    }
  }

  #updateModalImage(category) {
    const images = document.querySelectorAll('.modal-category-image');
    images.forEach(img => {
      const imgCategory = img.getAttribute('data-category');
      if (imgCategory === category) {
        img.classList.add('active');
      } else {
        img.classList.remove('active');
      }
    });
  }

  #initActiveStates() {
    const categoryActive = document.querySelector('button[data-category].active');
    const unitActive = document.querySelector('button[data-unit].active');
    if (categoryActive) this.#moveActiveState(categoryActive);
    if (unitActive) this.#moveActiveState(unitActive);
  }

  #moveActiveState(activeButton) {
    const container = activeButton.closest('.toggle-buttons');
    const state = container?.querySelector('.toggle-buttons__active-state');
    if (!container || !state) return;

    // Check if the container is visible (getBoundingClientRect returns 0 dimensions when display:none)
    const containerRect = container.getBoundingClientRect();
    if (containerRect.width === 0 || containerRect.height === 0) return;

    const br = activeButton.getBoundingClientRect();
    const cr = containerRect;
    const translateX = br.left - cr.left;
    
    state.style.transform = `translateX(${translateX}px)`;
    state.style.width = `${br.width}px`;
    state.style.height = `${br.height}px`;
  }

  // Public: reposition highlights for current active buttons
  repositionActiveStates() {
    const categoryActive = document.querySelector('button[data-category].active');
    const unitActive = document.querySelector('button[data-unit].active');
    if (categoryActive) this.#moveActiveState(categoryActive);
    if (unitActive) this.#moveActiveState(unitActive);
  }

  #renderCategory(category) {
    // Render a specific category (for page layout)
    const area = document.querySelector(`[data-category-content="${category}"]`);
    if (!area) return;

    if (!this.contentData) {
      area.innerHTML = '<p class="size-guide-modal__not-found">Size chart data not configured.</p>';
      return;
    }

    const unit = this.categoryUnits[category];
    const key = `${category}-${unit}`;
    const openTag = `[${key}]`;
    const closeTag = `[/${key}]`;

    const start = this.contentData.indexOf(openTag);
    const end = this.contentData.indexOf(closeTag);

    if (start === -1 || end === -1) {
      // Unit fallback (e.g., prefer IN, show CM if IN missing)
      const altUnit = unit === 'cm' ? 'in' : 'cm';
      const altKey = `${category}-${altUnit}`;
      const altOpen = `[${altKey}]`;
      const altClose = `[/${altKey}]`;
      const aStart = this.contentData.indexOf(altOpen);
      const aEnd = this.contentData.indexOf(altClose);

      if (aStart !== -1 && aEnd !== -1) {
        const html = this.contentData.substring(aStart + altOpen.length, aEnd).trim();
        area.innerHTML = html;
        this.categoryUnits[category] = altUnit;
        this.#updateActiveButtons(`[data-category-unit="${category}"] [data-unit]`, 'data-unit', altUnit);
        return;
      }

      area.innerHTML = '<p class="size-guide-modal__not-found">Size chart not available for this selection.</p>';
      return;
    }

    const html = this.contentData.substring(start + openTag.length, end).trim();
    area.innerHTML = html;
  }

  #render() {
    // Original render for modal layout
    const area = document.getElementById('size-guide-content');
    if (!area) return;

    if (!this.contentData) {
      area.innerHTML = '<p class="size-guide-modal__not-found">Size chart data not configured.</p>';
      return;
    }

    const key = `${this.activeCategory}-${this.activeUnit}`;
    const openTag = `[${key}]`;
    const closeTag = `[/${key}]`;

    const start = this.contentData.indexOf(openTag);
    const end = this.contentData.indexOf(closeTag);

    if (start === -1 || end === -1) {
      // Unit fallback (e.g., prefer CM, show IN if CM missing)
      const altUnit = this.activeUnit === 'cm' ? 'in' : 'cm';
      const altKey = `${this.activeCategory}-${altUnit}`;
      const altOpen = `[${altKey}]`;
      const altClose = `[/${altKey}]`;
      const aStart = this.contentData.indexOf(altOpen);
      const aEnd = this.contentData.indexOf(altClose);

      if (aStart !== -1 && aEnd !== -1) {
        const html = this.contentData.substring(aStart + altOpen.length, aEnd).trim();
        area.innerHTML = html;
        this.activeUnit = altUnit;
        this.#updateActiveButtons('[data-unit]', 'data-unit', this.activeUnit);
        this.#syncCategorySelect();
        return;
      }

      area.innerHTML = '<p class="size-guide-modal__not-found">Size chart not available for this selection.</p>';
      return;
    }

    const html = this.contentData.substring(start + openTag.length, end).trim();
    area.innerHTML = html;
  }
}

// Initialize controller and expose
window.__sgCtrl = new SizeGuideModalController();

// Recalculate active-state when size guide dialog opens
document.addEventListener('dialog:open', (e) => {
  if (e?.target?.id !== 'size-guide-modal') return;
  const ctrl = window.__sgCtrl;
  if (!ctrl) return;
  
  // Disable transition for instant positioning on open
  const activeStates = document.querySelectorAll('.toggle-buttons__active-state');
  activeStates.forEach(state => state.style.transition = 'none');
  
  // Position immediately and with multiple delays to ensure DOM has settled
  requestAnimationFrame(() => {
    ctrl.repositionActiveStates();
    
    // Re-enable transitions after a short delay
    setTimeout(() => {
      activeStates.forEach(state => state.style.transition = '');
    }, 50);
    
    // Additional positioning attempts
    setTimeout(() => ctrl.repositionActiveStates(), 10);
    setTimeout(() => ctrl.repositionActiveStates(), 100);
  });
});

// Public API - open modal via existing CTA
window.openSizeGuide = function () {
  const modal = document.querySelector('#size-guide-modal');
  if (modal && typeof modal.showDialog === 'function') {
    modal.showDialog();
    const ctrl = window.__sgCtrl;
    
    // Disable transition for instant positioning on open
    const activeStates = document.querySelectorAll('.toggle-buttons__active-state');
    activeStates.forEach(state => state.style.transition = 'none');
    
    // Position immediately and with multiple delays to ensure DOM has settled
    requestAnimationFrame(() => {
      ctrl && ctrl.repositionActiveStates();
      
      // Re-enable transitions after a short delay
      setTimeout(() => {
        activeStates.forEach(state => state.style.transition = '');
      }, 50);
      
      // Additional positioning attempts
      setTimeout(() => ctrl && ctrl.repositionActiveStates(), 10);
      setTimeout(() => ctrl && ctrl.repositionActiveStates(), 100);
    });
  }
};

// Ensure active states are positioned on page load (for size guide page layout)
window.addEventListener('load', () => {
  const ctrl = window.__sgCtrl;
  if (ctrl) {
    setTimeout(() => ctrl.repositionActiveStates(), 100);
  }
});

